/**
 * A run as a peek: its steps as a waterfall on one time axis, each with the documents it stored. The leg up to a
 * step is the work (violet); a leg longer than a minute is waiting (grey); a failed step is red. The status logic
 * (failed first, then on hold, then done) is ported from components/data/Runs.tsx.
 *
 * The small bits the other peeks share (status dot and word, the document chip, the section kicker with its fact)
 * live here, so the kind files form a straight line and the container imports them all.
 */
import { useMemo, type ReactNode } from 'react';
import dayjs from 'dayjs';
import { Api, type ExecutionStep, type Hit } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useAsync } from '../lib/hooks.ts';
import { count, duration } from '../lib/format.ts';
import { stripSystem, systemOf } from '../lib/systems.ts';
import { Kicker, NounIcon, SystemBadge, cx, noun } from '../design/primitives.tsx';
import type { MessageRef } from './usePeek.ts';

// ---------------------------------------------------------------- shared bits

/** The status a run ends up with: failed if any document failed, on hold if any is, done if any succeeded, otherwise the most common. */
export function runStatus(statuses: Record<string, number>, failed = statuses.FAILED ?? 0): string | undefined {
  if (failed > 0) return 'FAILED';
  if (statuses.HOLD) return 'HOLD';
  if (statuses.SUCCESS) return 'SUCCESS';
  return Object.entries(statuses).sort((a, b) => b[1] - a[1])[0]?.[0];
}

const DOT: Record<string, string> = { SUCCESS: 'bg-ok', FAILED: 'bg-fail', HOLD: 'bg-warn', NEW: 'bg-accent', PROCESSING: 'bg-accent' };
const WORD: Record<string, string> = { SUCCESS: 'done', FAILED: 'failed', HOLD: 'on hold', NEW: 'new', PROCESSING: 'processing' };

/** The Tailwind background of a status dot (status is a dot, never a fill). */
export const statusDot = (status: string | null | undefined) => DOT[status ?? ''] ?? 'bg-n6';
export const statusWord = (status: string | null | undefined) => WORD[status ?? ''] ?? (status ? status.toLowerCase() : 'no status');

/** A status as a dot and a word: "● done", "● failed". */
export function StatusMark({ status, className }: { status: string | null | undefined; className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 text-small whitespace-nowrap', status === 'FAILED' ? 'text-fail' : 'text-n9', className)}>
      <i className={cx('size-2 rounded-full shrink-0', statusDot(status))} aria-hidden="true" />
      {statusWord(status)}
    </span>
  );
}

/** The micro line above a section, carrying a fact on the right: "WHERE · 2 addresses". */
export function KickerRow({ title, fact, warn }: { title: ReactNode; fact?: ReactNode; warn?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 pb-1.5">
      <Kicker className="truncate">{title}</Kicker>
      {fact !== undefined && fact !== null && (
        // Kicker's own type, in --warn when the fact is a disagreement
        <span className={cx('text-micro font-medium uppercase tracking-[.08em] text-right shrink-0', warn ? 'text-warn' : 'text-n7')}>{fact}</span>
      )}
    </div>
  );
}

/** A document as a small chip: the noun of its data model with its status dot; click opens it. */
export function DocChip({ cls, status, onClick, className }: { cls: string; status: string | null | undefined; onClick: () => void; className?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={cx('inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-chip bg-n3 text-small text-n9 border-0 cursor-pointer max-w-full',
        'transition-colors duration-[var(--t-micro)] ease-mv hover:bg-n4 hover:text-n10', className)}>
      <NounIcon kind="document" size={14} />
      <span className="truncate">{noun(cls)}</span>
      <i className={cx('size-1.5 rounded-full shrink-0', statusDot(status))} aria-hidden="true" />
    </button>
  );
}

// ---------------------------------------------------------------- the run

/** A leg longer than this is waiting (a schedule, a retry, a human), not work. */
const WAIT_MS = 60_000;

interface Leg { step: ExecutionStep; from: number; to: number; wait: boolean; failed: boolean }

/** The steps in time order, each with the leg that led to it (the first has none). */
function legsOf(steps: ExecutionStep[]): Leg[] {
  const sorted = [...steps].sort((a, b) => a.timestamp - b.timestamp);
  return sorted.map((step, i) => {
    const prev = sorted[i - 1];
    const from = step.elapsedMs !== undefined && step.elapsedMs > 0 ? step.timestamp - step.elapsedMs : prev?.timestamp ?? step.timestamp;
    const failed = step.status === 'FAILED' || step.items.some((h) => h.meta.status === 'FAILED');
    return { step, from: Math.min(from, step.timestamp), to: step.timestamp, wait: step.timestamp - from >= WAIT_MS, failed };
  });
}

export function RunPeek({ executionId, onMessage }: { executionId: string; onMessage: (ref: MessageRef) => void }) {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const res = useAsync(() => Api.execution(tid, executionId), [tid, executionId]);
  const ex = res.data;

  const legs = useMemo(() => legsOf(ex?.steps ?? []), [ex]);
  const items = useMemo(() => legs.flatMap((l) => l.step.items), [legs]);
  const statuses = useMemo(() => {
    const out: Record<string, number> = {};
    for (const l of legs) {
      const its = l.step.items.length ? l.step.items.map((h) => h.meta.status ?? l.step.status) : [l.step.status];
      for (const s of its) if (s) out[s] = (out[s] ?? 0) + 1;
    }
    return out;
  }, [legs]);
  const process = items.find((h) => h.meta.process)?.meta.process ?? null;
  const system = systemOf(process) ?? systemOf(items.find((h) => h.meta.connection)?.meta.connection) ?? null;
  const first = legs.length ? Math.min(legs[0].from, legs[0].to) : 0;
  const last = legs.length ? legs[legs.length - 1].to : 0;
  const span = Math.max(1, last - first);
  const x = (t: number) => ((t - first) / span) * 100;
  const docs = ex?.messages ?? items.length;
  // The run's day once, on the axis; the rows carry the time of day only. A run over midnight dates its end too.
  const sameDay = dayjs(first).isSame(dayjs(last), 'day');
  const today = dayjs(first).isSame(dayjs(), 'day');
  const time = (t: number) => dayjs(t).format('HH:mm:ss');

  if (res.error) return <p className="m-0 pt-2 text-small text-fail">{res.error}</p>;
  if (!ex) return <RunSkeleton />;

  return (
    <article className="flex flex-col gap-6 pt-2">
      <header className="flex flex-col gap-2">
        <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{stripSystem(process) || 'A run'}</h2>
        <div className="flex items-center gap-2.5 flex-wrap text-small text-n8">
          {system && <SystemBadge code={system} size={20} />}
          <StatusMark status={runStatus(statuses)} />
          {legs.length > 0 && (
            <span className="font-mono text-n7 whitespace-nowrap">
              {!today && `${dayjs(first).format('D.M.')} `}{time(first)} → {!sameDay && `${dayjs(last).format('D.M.')} `}{time(last)}
              {last > first && <span className="text-n6"> · {duration(last - first)}</span>}
            </span>
          )}
          <span className="whitespace-nowrap">{count(docs)} document{docs === 1 ? '' : 's'}</span>
        </div>
        <p className="m-0 font-mono text-micro text-n7 truncate">{executionId}</p>
        {ex.partial && <p className="m-0 text-micro text-warn">Some vaults did not answer; the run may have more steps.</p>}
      </header>

      <section>
        <KickerRow title="Steps" fact={`${count(legs.length)} step${legs.length === 1 ? '' : 's'}${last > first ? ` · ${duration(last - first)}` : ''}`} />
        {legs.length === 0 && <p className="m-0 text-small text-n8">No steps were stored for this run.</p>}
        {legs.length > 0 && (
          <>
            <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 pb-1">
              <span className="font-mono text-micro text-n7 whitespace-nowrap">{dayjs(first).format('ddd D MMM')}</span>
              <div className="relative h-4 font-mono text-micro text-n7">
                <span className="absolute left-0">{time(first)}</span>
                {last > first && <span className="absolute right-0">{!sameDay && `${dayjs(last).format('ddd D MMM')} `}{time(last)}</span>}
              </div>
            </div>
            <ol className="m-0 p-0 list-none flex flex-col">
              {legs.map((l, i) => {
                const h = l.step;
                const tone = l.failed ? 'var(--fail)' : 'var(--c-run)';
                const left = x(l.from);
                const width = Math.max(0, x(l.to) - left);
                return (
                  <li key={`${h.correlationId ?? i}-${h.timestamp}`} className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 border-t border-n4 py-2.5 mv-rise"
                    style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
                    <div className="font-mono text-micro text-n7 pt-1 whitespace-nowrap">
                      {time(h.timestamp)}
                      {l.to > l.from && <span className="block text-n6">+{duration(l.to - l.from)}</span>}
                    </div>
                    <div className="flex flex-col gap-1.5 min-w-0">
                      <div className="relative h-2.5 border-b border-n3" aria-hidden="true">
                        {width > 0 && (
                          <span className="absolute inset-y-0 rounded-full" style={{ left: `${left}%`, width: `max(4px, ${width}%)`, background: l.wait ? 'var(--n4)' : tone }} />
                        )}
                        <span className="absolute inset-y-0 w-1 rounded-full" style={{ left: `min(calc(100% - 4px), ${x(l.to)}%)`, background: tone }} />
                      </div>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-small font-medium text-n10">{noun(h.messageClass, h.items.length > 1 ? 2 : 1)}</span>
                        <StatusMark status={h.status ?? h.items[0]?.meta.status} />
                        {h.items.length > 1 && <span className="text-micro text-n7">{count(h.items.length)} documents at once</span>}
                      </div>
                      {h.info && <p className={cx('m-0 text-small', l.failed ? 'text-fail' : 'text-n8')}>{h.info}</p>}
                      {h.items.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {h.items.slice(0, 12).map((d: Hit) => (
                            <DocChip key={d.uuid} cls={d.messageClass} status={d.meta.status} onClick={() => onMessage({ vaultId: d.vaultId ?? h.vaultId, uuid: d.uuid, day: d.day })} />
                          ))}
                          {h.items.length > 12 && <span className="text-micro text-n7 self-center">+ {count(h.items.length - 12)}</span>}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </section>
    </article>
  );
}

/** The run's layout before its data: a title, a facts line, three legs. */
function RunSkeleton() {
  return (
    <div className="flex flex-col gap-6 pt-2" aria-busy="true">
      <div className="flex flex-col gap-2">
        <span className="h-7 w-56 rounded-chip mv-shimmer" />
        <span className="h-4 w-72 rounded-chip mv-shimmer" />
        <span className="h-3 w-80 rounded-chip mv-shimmer" />
      </div>
      <div className="flex flex-col gap-3">
        <span className="h-3 w-24 rounded-chip mv-shimmer" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 border-t border-n4 py-2.5">
            <span className="h-3 w-12 rounded-chip mv-shimmer" />
            <div className="flex flex-col gap-2">
              <span className="h-2.5 rounded-full mv-shimmer" style={{ width: `${30 + i * 20}%`, marginLeft: `${i * 25}%` }} />
              <span className="h-4 w-40 rounded-chip mv-shimmer" />
              <span className="h-7 w-32 rounded-chip mv-shimmer" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
